import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Moment} from 'moment';
import {DateRangePicker} from '@forecast-it/design-system';
import {dateObjectToIsoDate, isoDateToMomentNonUtc} from '../../../../forecast-app/shared/util/DateUtil';

export const DATE_RANGE_CHANGE_DEBOUNCE_MS = 400;

interface TimeMaterialDateRangeProps {
	startDate: Moment;
	endDate: Moment;
	onChange: (start: Moment, end: Moment) => void;
}

/**
 * The picker reports every segment edit (typing a year reports each digit) and also reports
 * ranges whose end is before their start. The picker keeps showing whatever the user typed, but
 * only a valid range is reported to onChange, once edits have paused for
 * DATE_RANGE_CHANGE_DEBOUNCE_MS.
 */
export const TimeMaterialDateRange = ({startDate, endDate, onChange}: TimeMaterialDateRangeProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const startIso = dateObjectToIsoDate(startDate);
	const endIso = dateObjectToIsoDate(endDate);
	const [draft, setDraft] = useState({startDate: startIso, endDate: endIso});
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const cancelPending = (): void => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	};

	useEffect(() => {
		setDraft({startDate: startIso, endDate: endIso});
	}, [startIso, endIso]);

	useEffect(() => cancelPending, []);

	const handleChange = ({startDate: newStart, endDate: newEnd}: {startDate: string; endDate: string}): void => {
		setDraft({startDate: newStart, endDate: newEnd});
		cancelPending();
		const start = isoDateToMomentNonUtc(newStart);
		const end = isoDateToMomentNonUtc(newEnd);
		if (!start || !end || end.isBefore(start, 'day')) {
			return;
		}
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			onChange(start, end);
		}, DATE_RANGE_CHANGE_DEBOUNCE_MS);
	};

	return (
		<DateRangePicker
			aria-label={formatMessage({id: 'common.dates'})}
			startDate={draft.startDate}
			endDate={draft.endDate}
			onChange={handleChange}
			offset={'l'}
		/>
	);
};
